import React, { useEffect, useState } from 'react';
import { TextField, Box, ToggleButton, ToggleButtonGroup, MenuItem } from '@mui/material';
import { maskCpfCnpj } from 'utils/banking';

type PersonType = 'PF' | 'PJ';

interface BeneficiaryFieldsProps {
  values: any;
  touched: any;
  errors: any;
  readOnly?: boolean;
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleBlur: (e: React.FocusEvent<any>) => void;
}

const categories = ['Motorista', 'Posto de Combustível', 'Oficina'];

const BeneficiaryFields: React.FC<BeneficiaryFieldsProps> = ({ values, touched, errors, readOnly, handleChange, handleBlur }) => {
  const [personType, setPersonType] = useState<PersonType>('PF');

  const isPJ = personType === 'PJ';

  const nameLabel = isPJ ? 'Razão Social *' : 'Nome Completo *';
  const namePlaceholder = isPJ ? 'Digite a razão social da empresa' : 'Digite o nome completo';

  const documentLabel = isPJ ? 'CNPJ *' : 'CPF *';
  const documentPlaceholder = isPJ ? '00.000.000/0000-00' : '000.000.000-00';

  const tradeLabel = isPJ ? 'Nome Fantasia *' : 'Apelido *';
  const tradePlaceholder = isPJ ? 'Digite o nome fantasia da empresa' : 'Informe um apelido ou nome abreviado';

  useEffect(() => {
    if (!values.documentNumber) return;

    const digits = values.documentNumber.replace(/\D/g, '');

    if (digits.length === 11) setPersonType('PF');
    if (digits.length === 14) setPersonType('PJ');

    const formatted = maskCpfCnpj(digits);

    if (formatted !== values.documentNumber) {
      handleChange({
        target: {
          name: 'documentNumber',
          value: formatted,
        },
      } as React.ChangeEvent<any>);
    }
  }, [values.documentNumber, handleChange]);

  return (
    <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
      <Box display="flex" justifyContent="center" sx={{ gridColumn: '1 / span 2' }}>
        <ToggleButtonGroup
          value={personType}
          exclusive
          onChange={(_, v) => v && setPersonType(v)}
          size="small"
          sx={{
            mb: 1,
            py: 1,
            px: 1,
            borderRadius: '16px',
            backgroundColor: '#F7F9FC',
            transition: 'box-shadow 0.2s, outline 0.2s',
            width: 'fit-content',
            '& .MuiToggleButton-root': {
              textTransform: 'none',
              border: 'none',
              borderRadius: '12px !important',
              px: 2,
            },
            '& .Mui-selected': {
              fontWeight: 600,
              backgroundColor: '#fff !important',
            },
            '& .Mui-disabled': {
              border: 'none !important',
            },
          }}
        >
          <ToggleButton value="PF" sx={{ textTransform: 'none' }} disabled={readOnly}>
            Pessoa Física
          </ToggleButton>

          <ToggleButton value="PJ" sx={{ textTransform: 'none' }}>
            Pessoa Jurídica
          </ToggleButton>
        </ToggleButtonGroup>
      </Box>

      <TextField
        label={documentLabel}
        placeholder={documentPlaceholder}
        fullWidth
        type="text"
        variant="standard"
        name="documentNumber"
        value={values.documentNumber}
        onChange={(e) => {
          const masked = maskCpfCnpj(e.target.value);
          handleChange({
            ...e,
            target: {
              ...e.target,
              value: masked,
              name: 'documentNumber',
            },
          });
        }}
        onBlur={handleBlur}
        error={Boolean(touched.documentNumber && errors.documentNumber)}
        helperText={touched.documentNumber && errors.documentNumber}
        InputLabelProps={{
          shrink: true,
          sx: { fontSize: '16px', fontWeight: 500 },
        }}
        inputProps={{ maxLength: 18, sx: { fontSize: '14px' } }}
        disabled={readOnly}
      />

      <TextField
        label={nameLabel}
        placeholder={namePlaceholder}
        fullWidth
        variant="standard"
        name="companyName"
        value={values.companyName}
        onChange={handleChange}
        onBlur={handleBlur}
        error={Boolean(touched.companyName && errors.companyName)}
        helperText={touched.companyName && errors.companyName}
        InputLabelProps={{
          shrink: true,
          sx: { fontSize: '16px', fontWeight: 500 },
        }}
        InputProps={{ sx: { fontSize: '14px' } }}
        disabled={readOnly}
      />

      <TextField
        label={tradeLabel}
        placeholder={tradePlaceholder}
        fullWidth
        variant="standard"
        name="tradeName"
        value={values.tradeName}
        onChange={handleChange}
        onBlur={handleBlur}
        error={Boolean(touched.tradeName && errors.tradeName)}
        helperText={touched.tradeName && errors.tradeName}
        InputLabelProps={{
          shrink: true,
          sx: { fontSize: '16px', fontWeight: 500 },
        }}
        InputProps={{ sx: { fontSize: '14px' } }}
        disabled={readOnly}
      />

      <TextField
        select
        label="Categoria"
        name="category"
        value={values.category || ''}
        onChange={handleChange}
        onBlur={handleBlur}
        variant="standard"
        fullWidth
        InputLabelProps={{
          shrink: true,
          sx: { fontSize: '16px', fontWeight: 500 },
        }}
        InputProps={{ sx: { fontSize: '14px' } }}
      >
        {categories.map((c) => (
          <MenuItem key={c} value={c}>
            {c}
          </MenuItem>
        ))}
      </TextField>
    </Box>
  );
};

export default BeneficiaryFields;
